import {
  CreateUser,
  DeleteUserAvatar,
  DeleteUserUser,
  GetUsers,
  ListUsers,
  RestoreUser,
  ShowUser,
  UpdateUser,
  UpdateUserAvatar,
} from '@/services/userService';
import { Query } from '@/types/query';
import { Response } from '@/types/response';
import { CreateUserForm, User } from '@/types/user';
import { handleError } from '@/utils/handleError';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface UserState {
  users: User[];
  user: User | null;
  filters: Query;
  loading: boolean;

  createUser: (user: CreateUserForm) => Promise<Response<User>>;
  listUsers: (query: Query) => Promise<void>;
  showUser: (id: number) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
  restoreUser: (id: number) => Promise<void>;
  updateUserAvatar: (avatar: string, userId: number) => Promise<void>;
  deleteUserAvatar: (userId: number) => Promise<void>;
  setFilters: (filters: Partial<Query>) => void;
  getUsers: (company_id?: number) => Promise<void>;
}

export const useUserStore = create<UserState>()(
  immer<UserState>(set => ({
    users: [],
    user: null,
    loading: false,
    filters: {
      paginate: 1,
      current_page: 1,
      per_page: 10,
      sort_field: 'id',
      sort_order: 'desc',
      search: '',
      total: 0,
      last_page: 1,
    },

    setFilters: (filters: Partial<Query>) => {
      set(state => {
        Object.assign(state.filters, filters);
      });
    },

    listUsers: async (query: Query) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { data } = await ListUsers(query);

        if ('pagination' in data) {
          const paginatedData = data as unknown as {
            data: User[];
            pagination: {
              per_page: number;
              current_page: number;
              last_page: number;
              total: number;
            };
          };
          set(state => {
            state.users =
              query.current_page === 1
                ? paginatedData.data
                : [...state.users, ...paginatedData.data];

            state.filters = {
              ...state.filters,
              per_page: paginatedData.pagination.per_page,
              current_page: paginatedData.pagination.current_page,
              last_page: paginatedData.pagination.last_page,
              total: paginatedData.pagination.total,
            };
          });
        } else {
          set(state => {
            state.users = data;
            state.filters.total = data.length;
            state.filters.per_page = data.length;
            state.filters.current_page = 1;
            state.filters.last_page = 1;
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    getUsers: async company_id => {
      try {
        set(state => {
          state.loading = true;
        });

        const { data } = await GetUsers(company_id);

        set(state => {
          state.users = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    createUser: async (user: CreateUserForm) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message, data } = await CreateUser(user);
        toast.success(message);

        set(state => {
          state.user = data;
          state.users.push(data);
        });

        return { data, message };
      } catch (error) {
        handleError(error);
        throw error;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    showUser: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { data } = await ShowUser(id);

        set(state => {
          state.user = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateUser: async (user: User) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message, data } = await UpdateUser(user);
        toast.success(message);

        set(state => {
          state.user = data;
          const index = state.users.findIndex(u => u.id === data.id);
          if (index !== -1) {
            Object.assign(state.users[index], data);
          }
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteUser: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message } = await DeleteUserUser(id);
        toast.success(message);

        set(state => {
          state.users = state.users.filter(user => user.id !== id);
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    restoreUser: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message, data } = await RestoreUser(id);
        toast.success(message);

        set(state => {
          const index = state.users.findIndex(user => user.id === id);
          if (index !== -1) {
            Object.assign(state.users[index], data);
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateUserAvatar: async (avatar: string, userId: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message, data } = await UpdateUserAvatar(avatar, userId);
        toast.success(message);

        set(state => {
          if (state.user?.id === userId) {
            state.user = data;
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteUserAvatar: async (userId: number) => {
      try {
        set(state => {
          state.loading = true;
        });

        const { message, data } = await DeleteUserAvatar(userId);
        toast.success(message);

        set(state => {
          if (state.user?.id === userId) {
            state.user = data;
          }
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },
  })),
);
